<template>
  <div
    ref="containerRef"
    class="book-container"
    :class="{
      'is-controls-hovered': isPC && controlsHovered,
      'book-container--mobile': !isPC,
      'book-container--immersive-fullscreen': isImmersiveFullscreen,
      'book-container--immersive-forced-landscape': isImmersiveFullscreen && shouldForceLandscape,
      'book-container--native-forced-landscape': nativeFullscreenShouldRotate
    }"
    :data-reader-rotation="nativeFullscreenShouldRotate ? '90' : undefined"
    @mouseenter="showControlsForMouse"
    @mousemove="showControlsForMouse"
    @pointerdown="handlePageTurnPointerDown"
    @pointerup="handlePageTurnPointerUp"
    @pointercancel="clearPageTurnPointerDown"
    @focusin="showControlsForMouse"
    @mouseleave="scheduleControlsHide"
  >
    <BookCanvas
      ref="bookCanvasRef"
      class="book-reader-canvas"
      :class="{
        'book-reader-canvas--mobile-landscape-excluded': mobileControlsExclusionActive
      }"
      :embedded="!!props.embedded"
    />

    <ReaderBgmControl
      v-if="isPC && hasBgm"
      :playing="isBgmPlaying"
      :muted="isMuted"
      :disabled="!canToggleBgm"
      @toggle="toggleBgmPlayback"
    />

    <Transition name="book-side-control">
      <button
        v-if="isPC"
        v-show="controlsVisible"
        type="button"
        class="book-side-control book-side-control--left"
        aria-label="Previous page"
        :disabled="!canGoPrev"
        @pointerenter.stop="setControlsHovered(true)"
        @pointerleave.stop="setControlsHovered(false)"
        @pointerdown.stop
        @pointerup.stop
        @click.stop="goPrev"
      >
        <BookControlIcon name="book-previous" :size="28" />
      </button>
    </Transition>

    <Transition name="book-side-control">
      <button
        v-if="isPC"
        v-show="controlsVisible"
        type="button"
        class="book-side-control book-side-control--right"
        aria-label="Next page"
        :disabled="!canGoNext"
        @pointerenter.stop="setControlsHovered(true)"
        @pointerleave.stop="setControlsHovered(false)"
        @pointerdown.stop
        @pointerup.stop
        @click.stop="goNext"
      >
        <BookControlIcon name="book-next" :size="28" />
      </button>
    </Transition>

    <BookControls
      v-if="isPC"
      ref="bookControlsRef"
      :visible="controlsVisible"
      :style="controlsPositionStyle"
      @prev="goPrev"
      @next="goNext"
      @share="shareBook"
      @toggle-fullscreen="toggleFullscreen"
      @toggle-zoom="toggleZoom"
      @toggle-play="togglePlay"
      @toggle-sound="toggleSound"
      @set-volume="setVolume"
      @hover-change="setControlsHovered"
    />

    <MobileBookControls
      v-else
      v-show="controlsVisible"
      :is-landscape="isMobileLandscape"
      :exclude-controls="excludeMobileControls"
      :style="mobileControlsStyle"
      @prev="goPrev"
      @next="goNext"
      @share="shareBook"
      @toggle-bgm="toggleBgmPlayback"
      @toggle-zoom="toggleZoom"
      @toggle-fullscreen="toggleFullscreen"
      @toggle-play="togglePlay"
      @toggle-sound="toggleSound"
      @set-volume="setVolume"
      @update:exclude-controls="setMobileControlsExclusion"
      @interaction-change="setMobileControlInteraction"
    />

    <BookSharePanel
      v-if="isPC"
      :visible="shareVisible"
      :title="projectStore.book.title ?? ''"
      :anchor-rect="shareAnchorRect"
      :teleport-target="containerRef ?? 'body'"
      @close="shareVisible = false"
    />
    <BookSharePanelMobile
      v-else
      :visible="shareVisible"
      :title="projectStore.book.title ?? ''"
      :teleport-target="containerRef ?? 'body'"
      @close="shareVisible = false"
    />

    <Transition name="book-click-guide">
      <BookClickGuideModal
        v-if="startupAudioUnlockRequired"
        :is-p-c="isPC"
        @confirm="handleStartupUnlockClick"
      />
    </Transition>

    <BookSwipeGuide
      v-if="swipeGuideVisible"
      :is-p-c="isPC"
      :embedded="!!props.embedded"
    />

    <BookBoundFlipToast ref="boundFlipToastRef" />

    <Transition name="book-loading">
      <div
        v-if="isBookLoading || isViewportLoading"
        class="book-loading-overlay"
        aria-live="polite"
        aria-busy="true"
        aria-label="加载中"
      >
        <BookLoading :variant="isPC ? 'pc' : 'mobile'" />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { useBookControlsPosition } from '@/hooks/useBookControlsPosition';
  import { useLockBrowserZoom } from '@/hooks/useLockBrowserZoom';
  import { useReaderBook } from '@/hooks/reader/useReaderBook';
  import { useReaderLayout } from '@/hooks/reader/useReaderLayout';
  import {
    readMobileControlsExclusionPreference,
    useReaderControls,
    writeMobileControlsExclusionPreference
  } from '@/hooks/reader/useReaderControls';
  import { useReaderAutoplay } from '@/hooks/reader/useReaderAutoplay';
  import { useReaderFullscreen } from '@/hooks/reader/useReaderFullscreen';
  import { useReaderLandscape } from '@/hooks/reader/useReaderLandscape';
  import { useReaderShare } from '@/hooks/reader/useReaderShare';
  import { resolveReaderBookLayout } from '@/hooks/reader/readerBookLayout';
  import { useProjectStore } from '@/store/project';
  import { useReaderControlsStore } from '@/store/readerControls';
  import { storeToRefs } from 'pinia';
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    watch,
    ref,
    type CSSProperties
  } from 'vue';
  import { useRoute } from 'vue-router';
  import BookCanvas from './BookCanvas.vue';
  import ReaderBgmControl from './ReaderBgmControl.vue';
  import BookControlIcon from './BookControlIcon.vue';
  import BookControls from './BookControls.vue';
  import MobileBookControls from './MobileBookControls.vue';
  import BookSharePanelMobile from './BookSharePanelMobile.vue';
  import BookSharePanel from './BookSharePanel.vue';
  import BookClickGuideModal from './BookClickGuideModal.vue';
  import BookSwipeGuide from './BookSwipeGuide.vue';
  import BookBoundFlipToast from './BookBoundFlipToast.vue';
  import BookLoading from '@/components/common/loading/BookLoading.vue';
  import { BookConstants } from '@/constants/bookConstants';
  import { EVENT_NAMES } from '@/constants';
  import { getPageTurnDirection } from '../pageTurnHitTest';
  import { emitter } from '@/utils/emitter';
  import { rememberContentEditorReturnUrl } from '@/utils/contentEditorReturn';
  import { clientPointToElement } from '@/utils/readerViewportCoordinates';
  import {
    PAGE_ERROR_EDIT_HISTORY_STATE_KEY,
    canShowPageErrorEdit,
    isPageErrorEditHistoryState,
    isPageErrorEditWindowName
  } from '../pageErrorEditAccess';

  const props = defineProps<{
    /** 嵌入到其他页面（如编辑器预览）时为 true：复用已加载的 projectStore 数据，不重新拉取/重置工程 */
    embedded?: boolean;
  }>();

  const containerRef = ref<HTMLElement | null>(null);
  const bookCanvasRef = ref<InstanceType<typeof BookCanvas> | null>(null);
  const bookControlsRef = ref<InstanceType<typeof BookControls> | null>(null);
  const boundFlipToastRef = ref<InstanceType<typeof BookBoundFlipToast> | null>(null);
  const startupCoverAudioCompleted = ref(false);
  const startupAudioReady = ref(false);
  const startupAudioUnlockRequired = ref(!props.embedded);
  const swipeGuideVisible = ref(false);
  const isViewportLoading = ref(false);

  const route = useRoute();
  const projectStore = useProjectStore();
  const readerControlsStore = useReaderControlsStore();
  readerControlsStore.reset();
  const {
    currentPageIndex,
    displayMode: activeDisplayMode,
    totalPages,
    currentPageLabel,
    isFirstPage,
    isLastPage,
    canGoPrev,
    canGoNext,
    isPlaying,
    playbackProgress: autoplayProgress,
    isZoomed,
    isFullscreen,
    muted: isMuted,
    hasBgm,
    isBgmPlaying,
    isBgmReady,
    canToggleBgm
  } = storeToRefs(readerControlsStore);

  useLockBrowserZoom();

  /** PC 端或横屏视口 */
  const { isPC, isLandscape, shouldForceLandscape, shouldRotateReader } = useReaderLandscape();

  const READER_BACKGROUND_PC_CLASS = 'reader-page-background--pc';
  const READER_BACKGROUND_MOBILE_CLASS = 'reader-page-background--mobile';

  const getReaderBackgroundTargets = () =>
    [document.documentElement, document.body, document.getElementById('app')].filter(
      (element): element is HTMLElement => element instanceof HTMLElement
    );

  const syncReaderPageBackground = (pc: boolean) => {
    getReaderBackgroundTargets().forEach((element) => {
      element.classList.toggle(READER_BACKGROUND_PC_CLASS, pc);
      element.classList.toggle(READER_BACKGROUND_MOBILE_CLASS, !pc);
    });
  };

  watch(isPC, syncReaderPageBackground, { immediate: true });

  /** 阅读画布（Pixi BookCanvas 实例），未挂载时为 undefined。 */
  const getCanvas = () => bookCanvasRef.value?.getCanvas();
  const getRouteProjectId = () => {
    const id = route.params.id;
    return Array.isArray(id) ? id[0] : (id as string | undefined);
  };
  const getEditorProjectId = () => getRouteProjectId() || String(projectStore.id || '');
  const normalizeWorkbenchBase = () => {
    let base = import.meta.env.VITE_DASHBOARD_PREFIX || ''.trim().replace(/^['"]|['"]$/g, '');
    if (!base || base === '/' || base === './' || base === '.') return '';
    if (!base.startsWith('/')) base = `/${base}`;
    return base.endsWith('/') ? base.slice(0, -1) : base;
  };
  const buildContentEditorUrl = (id: string) => {
    const domain = String(import.meta.env.VITE_WEB_DOMAIN ?? '').trim();
    const origin = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
    const url = new URL(`${origin}${normalizeWorkbenchBase()}/edit/${encodeURIComponent(id)}`);
    return url.toString();
  };
  const showPageErrorEditMessage = (_type: 'success' | 'error', message: string) => {
    boundFlipToastRef.value?.show(message);
  };

  /** 首末页边界提示节流，避免滚轮/连滑刷屏。 */
  let boundFlipMessageAt = 0;
  const BOUND_FLIP_MESSAGE_COOLDOWN_MS = 1500;

  /** 用户朝边界外继续翻页时，在阅读器容器内展示提示（避开 ElMessage 被层级压住）。 */
  const showBoundFlipMessage = (direction: 'forward' | 'back') => {
    const now = Date.now();
    if (now - boundFlipMessageAt < BOUND_FLIP_MESSAGE_COOLDOWN_MS) return;
    boundFlipMessageAt = now;
    const text = direction === 'back' ? '这是第一页了。' : '这是最后一页了。';
    boundFlipToastRef.value?.show(text, direction);
  };
  const copyText = async (text: string): Promise<boolean> => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fall through to the textarea fallback for browsers that expose but block Clipboard API.
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  };
  const openContentEditor = async () => {
    const id = getEditorProjectId();
    if (!id) {
      showPageErrorEditMessage('error', '链接不可用，请稍后重试');
      return;
    }

    const editorUrl = buildContentEditorUrl(id);
    if (!isPC.value) {
      const copied = await copyText(editorUrl);
      if (copied) {
        showPageErrorEditMessage('success', '链接已复制');
        return;
      }

      showPageErrorEditMessage('error', '复制失败，请长按链接手动复制');
      return;
    }

    rememberContentEditorReturnUrl();
    window.location.href = editorUrl;
  };
  const fromAllowedPageErrorEditEntry = computed(() => {
    if (typeof window === 'undefined') return false;
    return (
      isPageErrorEditHistoryState(window.history.state?.[PAGE_ERROR_EDIT_HISTORY_STATE_KEY]) ||
      isPageErrorEditWindowName(window.name)
    );
  });
  const canPageErrorEdit = () =>
    canShowPageErrorEdit({
      embedded: !!props.embedded,
      fromAllowedEntry: fromAllowedPageErrorEditEntry.value
    });
  const getPageErrorEditLabel = () => (isPC.value ? '前往内容编辑器' : '前往电脑端内容编辑器');

  const canvasSize = computed(() => {
    const actualSize = bookCanvasRef.value?.getCanvasSize();
    if (actualSize && actualSize.width > 0 && actualSize.height > 0) {
      return actualSize;
    }

    return { width: 0, height: 0 };
  });

  const isMobileLandscape = computed(
    () => !isPC.value && (isLandscape.value || shouldRotateReader.value)
  );
  const excludeMobileControls = ref(readMobileControlsExclusionPreference());
  const mobileControlsExclusionActive = computed(
    () => isMobileLandscape.value && excludeMobileControls.value
  );

  const canvasElementRef = computed(() => bookCanvasRef.value?.getCanvasElement() ?? null);

  /** 把 store 里的静音/音量同步到画布（作用于全部书本音频）。 */
  const applyAudioSettings = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    canvas.setMasterMuted(projectStore.muted);
    canvas.setMasterVolume(projectStore.volume);
  };

  let startupUnlockArmed = false;
  let startupAudioGestureReceived = false;
  const SWIPE_GUIDE_SHOWN_KEY = 'storybook:swipe-guide-shown';
  let swipeGuideShown = false;
  let swipeGuideTimer: number | null = null;

  /** 清除翻页引导定时器 */
  const clearSwipeGuideTimer = () => {
    if (swipeGuideTimer === null) return;
    window.clearTimeout(swipeGuideTimer);
    swipeGuideTimer = null;
  };

  /** 读取本地是否已展示过翻页引导 */
  const hasSwipeGuideShown = () => {
    try {
      return window.localStorage.getItem(SWIPE_GUIDE_SHOWN_KEY) === '1';
    } catch {
      return false;
    }
  };

  /** 将翻页引导标记为已展示（同浏览器只显示一次） */
  const markSwipeGuideShown = () => {
    try {
      window.localStorage.setItem(SWIPE_GUIDE_SHOWN_KEY, '1');
    } catch {
      // 隐私模式等无法写入时忽略，本次会话仍靠 swipeGuideShown 防重复
    }
  };

  /** 显示翻页引导：本地无记录时才展示，展示后写入 localStorage */
  const showSwipeGuide = () => {
    // 步骤1：本次会话或本地已展示过则跳过
    if (swipeGuideShown || hasSwipeGuideShown()) return;

    // 步骤2：标记并展示引导
    swipeGuideShown = true;
    markSwipeGuideShown();
    swipeGuideVisible.value = true;
    clearSwipeGuideTimer();
    // 步骤3：2 秒后自动隐藏
    swipeGuideTimer = window.setTimeout(() => {
      swipeGuideVisible.value = false;
      swipeGuideTimer = null;
    }, 2000);
  };

  const clearStartupAudioUnlock = () => {
    startupUnlockArmed = false;
    window.removeEventListener('pointerdown', handleStartupUnlockPointerDown, true);
  };

  const armStartupAudioUnlock = () => {
    if (startupUnlockArmed) return;
    startupUnlockArmed = true;
    window.addEventListener('pointerdown', handleStartupUnlockPointerDown, true);
  };

  const handleStartupAudioBlocked = () => {
    if (props.embedded) return;

    startupAudioUnlockRequired.value = true;
    armStartupAudioUnlock();
  };

  const handleStartupAudioStarted = () => {
    startupAudioUnlockRequired.value = false;
    clearStartupAudioUnlock();
  };

  const attemptStartupAudio = (fromUserGesture: boolean) => {
    const canvas = getCanvas();
    if (!canvas || !startupAudioReady.value) return false;

    if (fromUserGesture) canvas.prepareStartupAudioForUserGesture();

    const attempted = canvas.playStartupAudio(
      handleStartupAudioBlocked,
      () => {
        startupCoverAudioCompleted.value = true;
      },
      handleStartupAudioStarted
    );

    if (!attempted) {
      startupAudioUnlockRequired.value = false;
      clearStartupAudioUnlock();
    }

    return attempted;
  };

  const unlockStartupAudio = () => {
    startupAudioGestureReceived = true;
    clearStartupAudioUnlock();
    startupAudioUnlockRequired.value = false;
    showSwipeGuide();

    if (!startupAudioReady.value) return;

    attemptStartupAudio(true);
  };

  const handleStartupUnlockPointerDown = (event: PointerEvent) => {
    if (!startupUnlockArmed || !event.isPrimary) return;

    unlockStartupAudio();
  };

  const handleStartupUnlockClick = () => {
    if (!startupAudioUnlockRequired.value) return;
    unlockStartupAudio();
  };

  const playStartupAudio = () => {
    startupAudioReady.value = true;
    startupCoverAudioCompleted.value = false;

    if (props.embedded) {
      startupAudioUnlockRequired.value = false;
      attemptStartupAudio(false);
      return;
    }

    if (!startupAudioGestureReceived) {
      startupAudioUnlockRequired.value = true;
      armStartupAudioUnlock();
      return;
    }

    startupAudioUnlockRequired.value = false;
    attemptStartupAudio(true);
  };

  const toggleBgmPlayback = () => {
    const canvas = getCanvas();
    if (!canvas || !canToggleBgm.value) return;

    if (isBgmPlaying.value) canvas.pauseBgm();
    else canvas.playBgm(false, armStartupAudioUnlock);
  };

  // ── 各功能域 composable，互不直接依赖，跨域联动统一在本文件装配 ──

  const {
    getCurrentBook,
    isBookLoading,
    getDisplayMode,
    prev: flipPrev,
    next: flipNext,
    resizeBook,
    initBook,
    refresh,
    destroy: destroyBook
  } = useReaderBook({
    embedded: !!props.embedded,
    isPC,
    isLandscape,
    currentPageIndex,
    activeDisplayMode,
    getCanvas,
    getProjectId: getRouteProjectId,
    getPageDisplaySize: (book, displayMode) => {
      const page = book.pages?.[0];
      const layout = resolveReaderBookLayout({
        canvasWidth: canvasSize.value.width,
        canvasHeight: canvasSize.value.height,
        isPC: isPC.value,
        pageWidth: book.width ?? page?.width ?? 0,
        pageHeight: book.height ?? page?.height ?? 0,
        displayMode,
        displayRect: book.displayRect
      });
      return layout ? { width: layout.pageWidth, height: layout.pageHeight } : null;
    },
    onPageErrorEdit: openContentEditor,
    canPageErrorEdit,
    getPageErrorEditLabel,
    // 翻页后尝试续播（若正处于末页翻回首页的循环流程中）
    onPageChange: () => tryBeginAutoplayAfterRestart(),
    // 用户在首末页继续滑动/翻页时提示边界
    onBoundFlipAttempt: ({ direction }) => showBoundFlipMessage(direction),
    // 末页播完后翻回首页继续循环
    onTimelineComplete: () => handleTimelineComplete(),
    // 新书就绪：布局定位 + 音量静音同步 + 缩放态监听
    onBookCreated: (book, displayMode) => {
      applyBookDisplayRect(book, displayMode);
      book.setClickFlipEnabled(isPC.value);
      applyAudioSettings();
      const canvas = getCanvas();
      const timeline = canvas?.getTimeline();
      if (timeline) {
        timeline.onUpdate = (progress) => {
          autoplayProgress.value = progress;
        };
      }
      canvas?.setBgmPlaybackStateHandler((playing) => {
        isBgmPlaying.value = playing;
      });
      canvas?.setBgmReadyStateHandler((ready) => {
        isBgmReady.value = ready;
        if (
          ready &&
          startupAudioReady.value &&
          startupAudioGestureReceived &&
          !isBgmPlaying.value
        ) {
          canvas.playBgm(true, handleStartupAudioBlocked, handleStartupAudioStarted);
        }
      });
      bindZoomStateListener();
    },
    onBookDisplayResize: (book, displayMode) => {
      applyBookDisplayRect(book, displayMode);
      getCanvas()?.renderNow();
    },
    onBookReady: () => {
      scheduleReaderLayoutSync(false);
      showControlsForCurrentViewport();
      playStartupAudio();
    },
    // 销毁前复位缩放监听与待续播标记
    onBeforeDestroy: () => {
      clearStartupAudioUnlock();
      stopAutoplay();
      startupAudioReady.value = false;
      startupAudioUnlockRequired.value = !props.embedded && !startupAudioGestureReceived;
      if (startupAudioUnlockRequired.value) armStartupAudioUnlock();
      const canvas = getCanvas();
      const timeline = canvas?.getTimeline();
      if (timeline) timeline.onUpdate = undefined;
      canvas?.pauseBgm();
      canvas?.setBgmPlaybackStateHandler(null);
      canvas?.setBgmReadyStateHandler(null);
      isBgmPlaying.value = false;
      isBgmReady.value = false;
      autoplayProgress.value = 0;
      startupCoverAudioCompleted.value = false;
      resetZoom();
      cancelPendingRestart();
    }
  });

  const {
    getBookLayout,
    applyBookDisplayRect,
    syncBookDisplayRect,
    getRenderedBookRect,
    bookContentBottom
  } = useReaderLayout({
    canvasSize,
    isPC,
    getCanvas,
    getCurrentBook,
    activeDisplayMode,
    totalPages
  });

  type MobileControlsStyle = CSSProperties & {
    '--mobile-book-top': string;
    '--mobile-book-height': string;
  };

  const mobileControlsStyle = computed<MobileControlsStyle | undefined>(() => {
    void currentPageLabel.value;
    void isBookLoading.value;
    const renderedBook = getRenderedBookRect();
    if (!renderedBook) return undefined;

    return {
      '--mobile-book-top': `${Math.round(renderedBook.rect.top)}px`,
      '--mobile-book-height': `${Math.round(renderedBook.rect.height)}px`
    };
  });

  /**
   * 将画布逻辑坐标系中的书本矩形映射到阅读容器 CSS 坐标后广播。
   * 横屏下 canvas 比容器窄，必须加上 canvas 相对容器的偏移。
   * 双页模式始终按整本打开宽度贴边（封面合上时也不缩到单页两侧）。
   */
  const emitBookDisplaySizeReady = () => {
    if (isPC.value) return;

    const renderedBook = getRenderedBookRect();
    const canvasElement = canvasElementRef.value;
    const container = containerRef.value;
    if (!renderedBook || !canvasElement || !container) return;
    if (renderedBook.canvasWidth <= 0 || renderedBook.canvasHeight <= 0) return;
    if (canvasElement.clientWidth <= 0 || canvasElement.clientHeight <= 0) return;

    // 1. 取当前可视矩形；双页模式再扩成整本打开宽度
    let { left: bookLeft, top: bookTop, width: bookWidth, height: bookHeight } =
      renderedBook.rect;
    if (activeDisplayMode.value === 'double') {
      const fullSpreadWidth = renderedBook.pageWidth * 2;
      const centerX = bookLeft + bookWidth / 2;
      bookLeft = centerX - fullSpreadWidth / 2;
      bookWidth = fullSpreadWidth;
    }

    // 2. 映射到阅读容器 CSS 坐标并广播
    const scaleX = canvasElement.clientWidth / renderedBook.canvasWidth;
    const scaleY = canvasElement.clientHeight / renderedBook.canvasHeight;
    const left = canvasElement.offsetLeft + bookLeft * scaleX;
    const top = canvasElement.offsetTop + bookTop * scaleY;
    const width = bookWidth * scaleX;
    const height = bookHeight * scaleY;

    emitter.emit(EVENT_NAMES.BOOK_DISPLAY_SIZE_READY, {
      left,
      top,
      width,
      height,
      containerWidth: container.clientWidth,
      containerHeight: container.clientHeight
    });
  };

  let readerLayoutFrame: number | null = null;
  let readerLayoutVersion = 0;
  let readerLayoutActive = true;

  const cancelReaderLayoutSync = () => {
    if (readerLayoutFrame !== null) {
      window.cancelAnimationFrame(readerLayoutFrame);
      readerLayoutFrame = null;
    }
  };

  const finishReaderLayout = (version: number) => {
    if (!readerLayoutActive || version !== readerLayoutVersion) return;
    isViewportLoading.value = false;
  };

  /**
   * 同一帧内只提交最后一次视口尺寸；异步清晰重绘再通过版本号丢弃旧结果。
   */
  const scheduleReaderLayoutSync = (showLoading = true) => {
    if (!readerLayoutActive) return;
    const version = ++readerLayoutVersion;
    cancelReaderLayoutSync();
    if (showLoading) isViewportLoading.value = true;

    readerLayoutFrame = window.requestAnimationFrame(() => {
      readerLayoutFrame = null;
      if (!readerLayoutActive || version !== readerLayoutVersion) return;

      const commitLayout = () => {
        if (!readerLayoutActive || version !== readerLayoutVersion) return;

        const { width, height } = canvasSize.value;
        const canvas = getCanvas();
        if (!canvas || width <= 0 || height <= 0) {
          finishReaderLayout(version);
          return;
        }

        canvas.syncViewportSize(width, height);

        if (!getCurrentBook()) {
          finishReaderLayout(version);
          return;
        }

        const nextDisplayMode = getDisplayMode();
        const displayModeChanged = activeDisplayMode.value !== nextDisplayMode;
        const layout = getBookLayout(nextDisplayMode);
        if (!layout) {
          finishReaderLayout(version);
          return;
        }

        projectStore.book.displayWidth = layout.pageWidth * (nextDisplayMode === 'double' ? 2 : 1);
        projectStore.book.displayHeight = layout.pageHeight;

        void resizeBook(layout.pageWidth, layout.pageHeight, nextDisplayMode)
          .then(() => {
            if (!readerLayoutActive || version !== readerLayoutVersion) return;
            syncBookDisplayRect();
            canvas.renderNow();
            if (displayModeChanged) syncAfterDisplayModeChange();
            emitBookDisplaySizeReady();
          })
          .finally(() => finishReaderLayout(version));
      };

      if (!showLoading) {
        commitLayout();
        return;
      }

      // 先让 Vue 提交遮罩并完成一帧绘制，再处理可能抖动的 renderer resize。
      readerLayoutFrame = window.requestAnimationFrame(() => {
        readerLayoutFrame = null;
        commitLayout();
      });
    });
  };

  const {
    controlsVisible,
    controlsHovered,
    showControlsForMouse,
    showControlsForCurrentViewport,
    hideControls,
    scheduleControlsHide,
    toggleControls,
    setControlsHovered,
    toggleZoom,
    bindZoomStateListener,
    resetZoom
  } = useReaderControls({
    getCanvas,
    isPC,
    isZoomed,
    keepVisible: mobileControlsExclusionActive
  });

  const setMobileControlsExclusion = async (enabled: boolean) => {
    excludeMobileControls.value = enabled;
    writeMobileControlsExclusionPreference(enabled);
    showControlsForCurrentViewport();
    await nextTick();
    bookCanvasRef.value?.remeasureCanvasSize?.();
    scheduleReaderLayoutSync(false);
  };

  const {
    togglePlay,
    restartAutoplayFromBeginning,
    stopAutoplay,
    tryBeginAutoplayAfterRestart,
    handleTimelineComplete,
    syncAfterDisplayModeChange,
    cancelPendingRestart
  } = useReaderAutoplay({
    getCanvas,
    getCurrentBook,
    isPlaying,
    isFirstPage,
    isLastPage,
    isStartupCoverAudioCompleted: () => startupCoverAudioCompleted.value,
    hideControls,
    showControlsForCurrentViewport
  });

  const { isNativeFullscreen, isImmersiveFullscreen, toggleFullscreen } = useReaderFullscreen({
      containerRef,
      forceLandscape: shouldForceLandscape,
      isFullscreen,
      showControlsForCurrentViewport
    });
  const nativeFullscreenShouldRotate = computed(
    () => isNativeFullscreen.value && shouldRotateReader.value
  );

  /** 未显式传锚点时，用控制条上的分享按钮位置兜底。 */
  const resolveShareAnchor = () => {
    const shareButton = (
      bookControlsRef.value as { $el?: HTMLElement } | null
    )?.$el?.querySelector<HTMLElement>('button[aria-label="Share"]');
    return shareButton?.getBoundingClientRect() ?? null;
  };

  const { shareVisible, shareAnchorRect, shareBook } = useReaderShare({
    showControlsForCurrentViewport,
    resolveShareAnchor
  });

  // ── 装配：翻页导航（动作 + 弹出控制条）、音频、点击翻页手势 ──

  const goPrev = () => {
    flipPrev();
    showControlsForCurrentViewport();
  };
  const goNext = () => {
    flipNext();
    showControlsForCurrentViewport();
  };

  const handleBookReplay = () => {
    restartAutoplayFromBeginning();
  };

  watch(() => [projectStore.volume, projectStore.muted], applyAudioSettings);

  watch(isPC, () => {
    shareVisible.value = false;
    getCurrentBook()?.setPageErrorEditLabel(getPageErrorEditLabel());
    getCurrentBook()?.setClickFlipEnabled(isPC.value);
  });

  const toggleSound = () => {
    projectStore.toggleMuted();
    showControlsForCurrentViewport();
  };

  const setVolume = (value: number) => {
    projectStore.volume = value;
    if (value > 0 && projectStore.muted) {
      projectStore.muted = false;
    }
    showControlsForCurrentViewport();
  };

  const mobileControlInteractionActive = ref(false);
  const setMobileControlInteraction = (active: boolean) => {
    mobileControlInteractionActive.value = active;
    clearPageTurnPointerDown();
    getCurrentBook()?.setManualFlipBlocked(active || isPlaying.value);
  };

  let pageTurnPointerDown: { pointerId: number; x: number; y: number } | null = null;

  const clearPageTurnPointerDown = () => {
    pageTurnPointerDown = null;
  };

  const handlePageTurnPointerDown = (event: PointerEvent) => {
    clearPageTurnPointerDown();
    if (mobileControlInteractionActive.value) return;
    // 播放期间不处理手动翻页。
    if (isPlaying.value) return;
    if (!event.isPrimary || event.button !== 0) return;

    pageTurnPointerDown = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY
    };
  };

  const handlePageTurnPointerUp = (event: PointerEvent) => {
    const pointerDown = pageTurnPointerDown;
    clearPageTurnPointerDown();
    if (!pointerDown || pointerDown.pointerId !== event.pointerId || !event.isPrimary) return;

    const distance = Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y);
    if (distance > BookConstants.CLICK_FLIP_DISTANCE_PX) {
      if (isPC.value && !getCanvas()?.isZooming()) hideControls();
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY;

    if (!isPC.value) {
      if (!mobileControlsExclusionActive.value) {
        window.setTimeout(() => {
          if (!getCanvas()?.isZooming()) toggleControls();
        }, 0);
      }
      return;
    }

    window.queueMicrotask(() => {
      if (getCanvas()?.isZooming()) return;

      // 四角交由 FlipController 的角自动翻页处理，这里跳过，避免同一次点击触发两次翻页。
      if (getCurrentBook()?.isPointOnFlipCorner(clientX, clientY)) {
        hideControls();
        return;
      }

      const canvasElement = canvasElementRef.value;
      const renderedBook = getRenderedBookRect();
      if (!canvasElement || !renderedBook) return;

      const canvasRect = canvasElement.getBoundingClientRect();
      if (canvasRect.width <= 0 || canvasRect.height <= 0) return;

      const point = clientPointToElement(
        canvasElement,
        clientX,
        clientY,
        renderedBook.canvasWidth,
        renderedBook.canvasHeight
      );
      const direction = getPageTurnDirection(point, renderedBook.rect, renderedBook.pageWidth);

      // 首末页仍调用翻页，由 onBoundFlipAttempt 统一提示
      if (direction === 'prev') {
        flipPrev();
        hideControls();
        return;
      }

      if (direction === 'next') {
        flipNext();
        hideControls();
        return;
      }

      if (controlsVisible.value) {
        hideControls();
        return;
      }

      showControlsForCurrentViewport();
    });
  };

  const { controlsPositionStyle } = useBookControlsPosition(
    containerRef,
    canvasElementRef,
    bookControlsRef,
    computed(() => [controlsVisible.value, canvasSize.value.width, canvasSize.value.height]),
    bookContentBottom
  );

  const initBookAfterCanvasReady = async () => {
    const bookCanvas = bookCanvasRef.value;
    if (!bookCanvas) return;

    try {
      await bookCanvas.canvasReady;
      if (!readerLayoutActive) return;
      await initBook();
    } catch (error) {
      if (readerLayoutActive) console.error(error);
    }
  };

  onMounted(() => {
    emitter.on(EVENT_NAMES.BOOK_REPLAY, handleBookReplay);

    // 嵌入（编辑器预览）模式下不在挂载时构建，改由弹窗每次打开时调用 refresh() 统一重建，
    // 确保每次打开都同步编辑端的最新改动。
    if (props.embedded) {
      showSwipeGuide();
      return;
    }

    armStartupAudioUnlock();
    void initBookAfterCanvasReady();
  });

  // keep-alive 场景：从列表切到其他绘本时，路由参数变化，重新加载真实数据
  watch(
    () => route.params.id,
    async (newId, oldId) => {
      if (props.embedded || !newId || newId === oldId) return;

      await initBookAfterCanvasReady();
    }
  );

  // 仅在视口 / PC 判定变化时重算书本缩放；控制条显隐不再触发，避免书本跳动
  watch(
    () => [canvasSize.value.width, canvasSize.value.height, isPC.value],
    () => {
      scheduleReaderLayoutSync();
    }
  );

  /**
   * 全屏进入 / 退出与横竖屏一样走一次布局同步：
   * 步骤 1：等 class / 原生全屏 DOM 落稳；步骤 2：主动重测画布；步骤 3：更新书本物理尺寸。
   */
  watch(isFullscreen, async () => {
    await nextTick();
    bookCanvasRef.value?.remeasureCanvasSize?.();
    scheduleReaderLayoutSync();
  });

  // 翻页导致单双页可视宽度变化时，通知移动端侧栏按钮重新贴边
  watch(currentPageLabel, () => {
    emitBookDisplaySizeReady();
  });

  watch(isMobileLandscape, () => {
    shareVisible.value = false;
    showControlsForCurrentViewport();
  });

  onBeforeUnmount(() => {
    emitter.off(EVENT_NAMES.BOOK_REPLAY, handleBookReplay);

    getReaderBackgroundTargets().forEach((element) => {
      element.classList.remove(READER_BACKGROUND_PC_CLASS, READER_BACKGROUND_MOBILE_CLASS);
    });

    readerLayoutActive = false;
    readerLayoutVersion += 1;
    cancelReaderLayoutSync();
    isViewportLoading.value = false;
    stopAutoplay();
    destroyBook();
    clearStartupAudioUnlock();
    clearSwipeGuideTimer();
    startupAudioReady.value = false;
    startupAudioUnlockRequired.value = false;
    swipeGuideVisible.value = false;
    startupAudioGestureReceived = false;
    if (!props.embedded) {
      projectStore.reset();
    }
    readerControlsStore.reset();
  });

  defineExpose({
    shareBook,
    refresh,
    stopAutoplay,
    destroy: destroyBook
  });
</script>

<style>
  html.reader-page-background--pc,
  body.reader-page-background--pc,
  #app.reader-page-background--pc {
    background-color: #f8fafc;
  }

  html.reader-page-background--mobile,
  body.reader-page-background--mobile,
  #app.reader-page-background--mobile {
    background-color: #000;
  }
</style>

<style scoped>
  .book-container {
    position: relative;
    width: 100%;
    height: 0;
    min-height: 0;
    flex: 1 1 0;
    background-color: #f8fafc;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    touch-action: pan-x pan-y;
  }

  .book-container--immersive-fullscreen {
    position: fixed;
    inset: 0;
    z-index: 1000;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    min-width: 0;
    box-sizing: border-box;
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
      env(safe-area-inset-left);
    background-color: #000;
  }

  .book-container--immersive-forced-landscape {
    width: 100%;
    height: 100%;
  }

  .book-container--native-forced-landscape {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    width: 100vh !important;
    width: 100dvh !important;
    height: 100vw !important;
    min-width: 0 !important;
    min-height: 0 !important;
    flex: none;
    transform: translate(-50%, -50%) rotate(90deg);
    transform-origin: center;
  }

  /* 不支持 dvh 的浏览器:用 JS 写入的 --app-dvh 兜底(见 utils/viewportUnitFallback.ts) */
  @supports not (height: 100dvh) {
    .book-container--immersive-fullscreen {
      height: calc(var(--app-dvh, 1vh) * 100);
    }

    /* 原生全屏强制横屏时宽度取「视口高度」,所以 width 用 --app-dvh */
    .book-container--native-forced-landscape {
      width: calc(var(--app-dvh, 1vh) * 100) !important;
    }
  }

  .book-container.is-controls-hovered .book-canvas-viewport {
    pointer-events: none;
  }

  .book-reader-canvas.book-reader-canvas--mobile-landscape-excluded {
    width: calc(100% - 120px);
    height: calc(100% - 25px);
    max-width: calc(100% - 120px);
    max-height: calc(100% - 25px);
    flex: 0 0 calc(100% - 120px);
    align-self: flex-start;
  }

  .book-side-control {
    position: absolute;
    top: 49%;
    z-index: 9;
    width: 50px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #160211;
    background: #fff;
    border: 0;
    border-radius: 50%;
    box-shadow:
      0 2px 6px rgba(0, 0, 0, 0.08),
      0 12px 28px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transform: translateY(-50%);
    transition:
      opacity 180ms ease,
      transform 180ms ease,
      box-shadow 180ms ease;
  }

  .book-side-control--left {
    left: 1.7%;
  }

  .book-side-control--right {
    right: 1.7%;
  }

  .book-side-control:hover:not(:disabled) {
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.1),
      0 16px 34px rgba(0, 0, 0, 0.14);
    transform: translateY(-50%) scale(1.04);
  }

  .book-side-control:focus-visible {
    outline: 2px solid #160211;
    outline-offset: 4px;
  }

  .book-side-control:disabled {
    color: #cccccc;
    opacity: 1;
    cursor: not-allowed;
  }

  .book-side-control-enter-active,
  .book-side-control-leave-active {
    transition:
      opacity 180ms ease,
      transform 180ms ease;
  }

  .book-side-control-enter-from,
  .book-side-control-leave-to {
    opacity: 0;
    transform: translateY(-50%) scale(0.92);
  }

  .book-side-control-enter-to,
  .book-side-control-leave-from {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }

  @media (min-width: 721px) {
    .book-container {
      min-width: 400px;
    }
  }

  /* 移动端阅读模式（含 iPad 竖屏强制横屏）统一黑底：
     由 isPC 状态驱动，与真实视口宽度/方向解耦，避免竖屏旋转后露出浅色白边 */
  .book-container--mobile {
    background-color: #000;
  }

  @media (prefers-reduced-motion: reduce) {
    .book-side-control,
    .book-side-control-enter-active,
    .book-side-control-leave-active,
    .book-click-guide-enter-active,
    .book-click-guide-leave-active {
      transition: none;
    }
  }

  .book-click-guide-enter-active,
  .book-click-guide-leave-active {
    transition: opacity 200ms ease;
  }

  .book-click-guide-enter-from,
  .book-click-guide-leave-to {
    opacity: 0;
  }

  .book-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8fafc;
  }

  /* 进入不淡入，点击刷新时遮罩需立刻可见；离开仍淡出避免闪断 */
  .book-loading-enter-active {
    transition: none;
  }

  .book-loading-leave-active {
    transition: opacity 200ms ease;
  }

  .book-loading-leave-to {
    opacity: 0;
  }

  /* 移动端阅读模式下加载遮罩同样用黑底，与容器保持一致 */
  .book-container--mobile .book-loading-overlay {
    background-color: #000;
  }
</style>
